import { layerClass } from "@freightsimple/ui-core/Theme/layers";
import { useEffect, useRef, useState } from "react";
import { CheckIcon } from "@phosphor-icons/react/Check";
import { XIcon } from "@phosphor-icons/react/X";
import { Icon } from "@freightsimple/ui-core/Icons/Icon";
import type { TimelineEntry } from "@freightsimple/observability/observability";
import { type Summary, fmtAttrs, fmtMs } from "./coldStartSummary";

// Non-engineering view for a slow cold load: a one-sentence prompt + a
// "Copy details" button whose payload is Slack/Linear-ready markdown.
// Engineers see ColdStartOverlayPanel instead.

// Keyframes are injected via a <style> tag because Tailwind v4 doesn't ship
// these animations and the notice is small enough that defining them inline
// avoids touching the global stylesheet.
const KEYFRAMES = `
@keyframes cold-start-friendly-enter {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes cold-start-friendly-exit {
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(16px); }
}
@keyframes cold-start-friendly-halo {
  /* Halo follows the foreground token so the pulse stays visible in dark
     mode (the literal rgba(26,26,46,0.25) light-mode value blends into a
     dark background). color-mix is supported by every browser that
     supports Tailwind v4. */
  0%, 100% { box-shadow: 0 0 0 0 color-mix(in oklab, var(--color-foreground) 25%, transparent); }
  50%      { box-shadow: 0 0 0 8px color-mix(in oklab, var(--color-foreground) 0%, transparent); }
}
@keyframes cold-start-friendly-press {
  0%   { transform: scale(1); }
  35%  { transform: scale(0.96); }
  100% { transform: scale(1); }
}
`;

const EXIT_DURATION_MS = 240;

interface Props {
  summary: Summary;
  entries: readonly TimelineEntry[];
  onDismiss: () => void;
}

export function ColdStartFriendlyNotice({
  summary,
  entries,
  onDismiss,
}: Props) {
  const [copied, setCopied] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [closing, setClosing] = useState(false);
  // Track the press + copied auto-reset timers so a rapid second click
  // doesn't double-fire and an unmount mid-animation doesn't call setState
  // on a dead component.
  const pressTimerRef = useRef<number | undefined>(undefined);
  const copiedTimerRef = useRef<number | undefined>(undefined);

  // When the user clicks the close icon we flip into `closing` so the exit
  // animation can run; only after it finishes do we tell the parent to
  // actually unmount us. Without this the card vanishes instantly and the
  // exit keyframes never get a chance to paint.
  useEffect(() => {
    if (!closing) return;
    const id = window.setTimeout(onDismiss, EXIT_DURATION_MS);
    return () => window.clearTimeout(id);
  }, [closing, onDismiss]);

  useEffect(() => {
    return () => {
      if (pressTimerRef.current !== undefined)
        window.clearTimeout(pressTimerRef.current);
      if (copiedTimerRef.current !== undefined)
        window.clearTimeout(copiedTimerRef.current);
    };
  }, []);

  async function handleCopy() {
    setPressed(true);
    if (pressTimerRef.current !== undefined)
      window.clearTimeout(pressTimerRef.current);
    pressTimerRef.current = window.setTimeout(() => setPressed(false), 220);
    try {
      await navigator.clipboard.writeText(
        buildSlowLoadReport(summary, entries),
      );
      setCopied(true);
      if (copiedTimerRef.current !== undefined)
        window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setCopied(false), 1_800);
    } catch {
      // Older browsers / locked-down environments may reject clipboard
      // writes. Clearing `copied` keeps the success state from showing —
      // the user can screenshot the notice instead.
      setCopied(false);
    }
  }

  const bootSeconds =
    summary.bootTotalMs !== undefined
      ? (summary.bootTotalMs / 1000).toFixed(1)
      : "—";

  const enterAnim =
    "cold-start-friendly-enter 320ms cubic-bezier(0.2, 0.9, 0.3, 1.1) both";
  const exitAnim = `cold-start-friendly-exit ${EXIT_DURATION_MS}ms ease-in both`;
  // The halo plays exactly 3 cycles (~5.4s) then settles — long enough to
  // catch the eye, short enough to not feel like a distraction.
  const haloAnim = "cold-start-friendly-halo 1.8s ease-out 0s 3";
  const pressAnim = "cold-start-friendly-press 220ms ease-out both";

  // Animation precedence on the Copy button:
  //   - press wins while pressed (instant tactile feedback)
  //   - halo plays for the first ~5s if nothing else is happening
  //   - both stop once copied so the success state reads as still
  const buttonAnimation = pressed ? pressAnim : copied ? undefined : haloAnim;

  return (
    <>
      <style>{KEYFRAMES}</style>
      {/* z-30 keeps the notice above page content but BELOW the dialog overlay
          (DIALOG_OVERLAY_BASE_Z = 40). A near-max z-index used to park it on
          top of everything, including open modals — and since both this notice
          and a dialog's primary action sit bottom-right, it covered the OK/Save
          button, so the action couldn't be clicked until the notice was
          dismissed. A transient slow-load notice must never block a focused
          modal; letting the modal cover it (it reappears on close) is correct. */}
      <div
        data-testid="cold-start-friendly-notice"
        className={`fixed right-3 bottom-3 ${layerClass.sticky} w-80 rounded-xl border-2 border-border bg-card p-4 text-foreground shadow-2xl`}
        style={{ animation: closing ? exitAnim : enterAnim }}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="text-sm font-semibold text-foreground">
            Hubble felt slow
          </div>
          <button
            type="button"
            onClick={() => setClosing(true)}
            aria-label="Dismiss slow load notice"
            className="cursor-pointer rounded p-0.5 text-content-faint transition-colors hover:text-content"
          >
            <Icon icon={XIcon} size="sm" />
          </button>
        </div>
        <div className="mt-1.5 text-[13px] leading-snug text-content-secondary">
          This load took{" "}
          <span className="font-semibold text-foreground">{bootSeconds}s</span>.
          If this happens often, please let engineering know — copy the details
          below and paste them in Slack or Linear.
        </div>
        {/* Mirrors Core Button's "primary neutral" variant — see
            buttonStyles.tsx. Custom because Core Button is inline-flex; we
            need full-width. */}
        <button
          type="button"
          onClick={handleCopy}
          className={`mt-3 w-full cursor-pointer rounded-md border-2 border-transparent px-3 py-1.5 text-xs font-bold text-background transition-colors ${
            copied ? "bg-green" : "bg-foreground hover:bg-foreground/80"
          }`}
          style={{ animation: buttonAnimation }}
        >
          <span className="inline-flex items-center justify-center gap-1.5">
            <span
              aria-hidden
              className="inline-flex overflow-hidden transition-all duration-200 ease-out"
              style={{
                maxWidth: copied ? 18 : 0,
                opacity: copied ? 1 : 0,
                transform: copied ? "scale(1)" : "scale(0.6)",
              }}
            >
              <Icon icon={CheckIcon} size="sm" weight="bold" />
            </span>
            <span>Copy details</span>
          </span>
        </button>
      </div>
    </>
  );
}

function buildSlowLoadReport(
  summary: Summary,
  entries: readonly TimelineEntry[],
): string {
  // Markdown bullet list — renders cleanly in both Slack and Linear when
  // pasted. Keys stay terse; the timeline is collapsed behind <details> so
  // the paste looks digestible at first glance.
  const connection =
    (navigator as Navigator & { connection?: { effectiveType?: string } })
      .connection?.effectiveType ?? "unknown";
  const lines = [
    "**Hubble slow load report**",
    "",
    `- boot.total: ${fmtMs(summary.bootTotalMs)}`,
    `- first_render: ${fmtMs(summary.firstRenderMs)}`,
    `- auth.init: ${fmtMs(summary.authInitMs)}`,
    `- msal.initialize: ${fmtMs(summary.msalInitializeMs)}`,
    `- silent_token: ${summary.silentSuccess}✓ / ${summary.silentRedirected + summary.silentRedirectFailed}↻ / ${summary.silentShortCircuit}∅ (${summary.silentAttempts} total)`,
    `- route: ${window.location.pathname}${window.location.search}`,
    `- connection: ${connection}`,
    `- userAgent: ${navigator.userAgent}`,
    `- timestamp: ${new Date().toISOString()}`,
    "",
    "<details><summary>timeline</summary>",
    "",
    "```",
    ...entries.map(
      (e) => `${fmtMs(e.t)} ${e.name}${e.attrs ? " " + fmtAttrs(e.attrs) : ""}`,
    ),
    "```",
    "</details>",
  ];
  return lines.join("\n");
}
